import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { issueTrackerApi, Issue, IssueStats } from '../services/issueTrackerApi';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  FileText,
  Filter,
  Search,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Ban,
  ArrowUpRight,
  RefreshCw,
  BarChart3
} from 'lucide-react';

// Issue type labels
const ISSUE_TYPE_LABELS: Record<string, string> = {
  'missing_document': 'Missing Document',
  'data_inconsistency': 'Data Inconsistency',
  'calculation_variance': 'Calculation Variance',
  'causation_unclear': 'Unclear Causation',
  'documentation_gap': 'Documentation Gap',
  'policy_coverage_question': 'Policy Coverage Question'
};

// Status configuration
const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: React.ElementType }> = {
  'pending_review': { label: 'Pending Review', color: 'text-yellow-700', bgColor: 'bg-yellow-50 border-yellow-200', icon: Clock },
  'approved': { label: 'Approved', color: 'text-blue-700', bgColor: 'bg-blue-50 border-blue-200', icon: CheckCircle },
  'rejected': { label: 'Rejected', color: 'text-gray-700', bgColor: 'bg-gray-50 border-gray-200', icon: XCircle },
  'sent_to_client': { label: 'Sent to Client', color: 'text-purple-700', bgColor: 'bg-purple-50 border-purple-200', icon: Send },
  'resolved': { label: 'Resolved', color: 'text-green-700', bgColor: 'bg-green-50 border-green-200', icon: CheckCircle },
  'wont_fix': { label: "Won't Fix", color: 'text-gray-500', bgColor: 'bg-gray-100 border-gray-300', icon: Ban }
};

// Priority configuration
const PRIORITY_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  'critical': { label: 'Critical', color: 'text-red-700', bgColor: 'bg-red-100' },
  'high': { label: 'High', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  'medium': { label: 'Medium', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  'low': { label: 'Low', color: 'text-gray-600', bgColor: 'bg-gray-100' }
};

export default function IssueTrackerDashboard() {
  const navigate = useNavigate();
  const [issues, setIssues] = useState<Issue[]>([]);
  const [stats, setStats] = useState<IssueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch issues and stats
  const fetchData = async () => {
    try {
      setError(null);
      const [issuesData, statsData] = await Promise.all([
        issueTrackerApi.getIssues({
          status: statusFilter !== 'all' ? statusFilter : undefined,
          priority: priorityFilter !== 'all' ? priorityFilter : undefined,
          issue_type: typeFilter !== 'all' ? typeFilter : undefined
        }),
        issueTrackerApi.getIssueStats()
      ]);
      setIssues(issuesData);
      setStats(statsData);
    } catch (err: any) {
      console.error('Failed to fetch issues:', err);
      setError(err.message || 'Failed to load issues');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter, priorityFilter, typeFilter]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Filter issues by search query
  const filteredIssues = issues.filter(issue => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      issue.title.toLowerCase().includes(query) ||
      issue.description.toLowerCase().includes(query) ||
      issue.report_id.toLowerCase().includes(query)
    );
  });

  // Group issues by status for kanban-style view
  const groupedByStatus = filteredIssues.reduce((acc, issue) => {
    const status = issue.status;
    if (!acc[status]) acc[status] = [];
    acc[status].push(issue);
    return acc;
  }, {} as Record<string, Issue[]>);

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-brand-primary" />
          <span className="ml-2 text-gray-600">Loading issues...</span>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="issue-tracker-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Issue Tracker</h1>
            <p className="text-sm text-gray-500 mt-1">Manage documentation gaps and data issues across cases</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/orchestrator')}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              Orchestrator
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              data-testid="refresh-issues-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700">{error}</span>
            <button onClick={handleRefresh} className="ml-auto text-red-600 hover:text-red-800 underline">
              Retry
            </button>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <StatCard
              label="Total Issues"
              value={stats.total}
              icon={BarChart3}
              color="text-gray-700"
              bgColor="bg-gray-50"
            />
            <StatCard
              label="Pending Review"
              value={stats.pending_review}
              icon={Clock}
              color="text-yellow-700"
              bgColor="bg-yellow-50"
            />
            <StatCard
              label="Approved"
              value={stats.approved}
              icon={CheckCircle}
              color="text-blue-700"
              bgColor="bg-blue-50"
            />
            <StatCard
              label="Sent to Client"
              value={stats.sent_to_client}
              icon={Send}
              color="text-purple-700"
              bgColor="bg-purple-50"
            />
            <StatCard
              label="Resolved"
              value={stats.resolved}
              icon={CheckCircle}
              color="text-green-700"
              bgColor="bg-green-50"
            />
            <StatCard
              label="Critical"
              value={stats.by_priority?.critical || 0}
              icon={AlertTriangle}
              color="text-red-700"
              bgColor="bg-red-50"
            />
          </div>
        )}

        {/* Search & Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search issues..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                data-testid="issue-search-input"
              />
            </div>
            
            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4" />
              Filters
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-primary"
                  data-testid="status-filter"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending_review">Pending Review</option>
                  <option value="approved">Approved</option>
                  <option value="sent_to_client">Sent to Client</option>
                  <option value="resolved">Resolved</option>
                  <option value="rejected">Rejected</option>
                  <option value="wont_fix">Won't Fix</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-primary"
                  data-testid="priority-filter"
                >
                  <option value="all">All Priorities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-primary"
                  data-testid="type-filter"
                >
                  <option value="all">All Types</option>
                  {Object.entries(ISSUE_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Issues List */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200 px-4 py-3 bg-gray-50">
            <h2 className="font-semibold text-gray-900">
              Issues ({filteredIssues.length})
            </h2>
          </div>
          
          {filteredIssues.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No issues found</h3>
              <p className="text-gray-500">
                {searchQuery || statusFilter !== 'all' || priorityFilter !== 'all' || typeFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Issues will appear here when detected during analysis'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredIssues.map((issue) => (
                <IssueRow
                  key={issue.id}
                  issue={issue}
                  onClick={() => navigate(`/issues/${issue.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

// Stat Card Component
function StatCard({ 
  label, 
  value, 
  icon: Icon, 
  color, 
  bgColor 
}: { 
  label: string; 
  value: number; 
  icon: React.ElementType; 
  color: string; 
  bgColor: string;
}) {
  return (
    <div className={`${bgColor} rounded-lg p-4 border border-gray-100`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs font-medium text-gray-600">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

// Issue Row Component
function IssueRow({ issue, onClick }: { issue: Issue; onClick: () => void }) {
  const statusConfig = STATUS_CONFIG[issue.status] || STATUS_CONFIG['pending_review'];
  const priorityConfig = PRIORITY_CONFIG[issue.priority] || PRIORITY_CONFIG['medium'];
  const StatusIcon = statusConfig.icon;

  return (
    <div
      onClick={onClick}
      className="px-4 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
      data-testid={`issue-row-${issue.id}`}
    >
      <div className="flex items-start gap-4">
        {/* Priority indicator */}
        <div className={`w-1 h-16 rounded-full ${priorityConfig.bgColor}`} />
        
        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${priorityConfig.bgColor} ${priorityConfig.color}`}>
              {priorityConfig.label}
            </span>
            <span className="text-xs text-gray-400">•</span>
            <span className="text-xs text-gray-500">
              {ISSUE_TYPE_LABELS[issue.issue_type] || issue.issue_type}
            </span>
          </div>
          
          <h3 className="font-medium text-gray-900 truncate">{issue.title}</h3>
          <p className="text-sm text-gray-500 truncate mt-0.5">{issue.description}</p>
          
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
            <span>Case: {issue.report_id.slice(0, 8)}...</span>
            <span>•</span>
            <span>Detected by: {issue.detected_by}</span>
            {issue.affected_amount && (
              <>
                <span>•</span>
                <span className="text-orange-600 font-medium">
                  Impact: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(issue.affected_amount)}
                </span>
              </>
            )}
          </div>
        </div>
        
        {/* Status badge & arrow */}
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${statusConfig.bgColor}`}>
            <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.color}`} />
            <span className={`text-xs font-medium ${statusConfig.color}`}>{statusConfig.label}</span>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>
      </div>
    </div>
  );
}
